import { instance } from '@app/api/axios';
import { useShowToaster } from '@shared/ui/messages/useToaster';
import { useRequestError } from '@app/api/useRequestError';
import { useDispatch } from 'react-redux';
import { fetchCards, setSelected } from '@app/store/slices/cardsSlice';

export function useCards() {
  const showToaster = useShowToaster();
  const handleRequestError = useRequestError();
  const dispatch = useDispatch();

  const refetchCardsLater = () => {
    setTimeout(() => {
      dispatch(fetchCards());
    }, 1500);
  };

  const handleResponse = ({ data }) => {
    const isSuccess = data?.success !== false && data?.status !== false;
    showToaster(isSuccess ? 'success' : 'error', data?.message);
    return isSuccess;
  };

  const createCard = async (data) => {
    try {
      const res = await instance.post('/card/create-card', data);
      if (!handleResponse(res)) return false;
      dispatch(fetchCards());
      return true;
    } catch (err) {
      return handleRequestError(err);
    }
  };

  const freezeCards = async (data) => {
    try {
      const res = await instance.post('/card/freeze', data);
      if (!handleResponse(res)) return false;
      refetchCardsLater();
      dispatch(setSelected([]));
      return true;
    } catch (err) {
      return handleRequestError(err, { rethrowValidation: false });
    }
  };

  const unfreezeCards = async (data) => {
    try {
      const res = await instance.post('/card/unfreeze', data);
      if (!handleResponse(res)) return false;
      refetchCardsLater();
      dispatch(setSelected([]));
      return true;
    } catch (err) {
      return handleRequestError(err, { rethrowValidation: false });
    }
  };

  const blockCards = async (data) => {
    try {
      const res = await instance.post('/card/block', data);
      if (!handleResponse(res)) return false;
      refetchCardsLater();
      dispatch(setSelected([]));
      return true;
    } catch (err) {
      return handleRequestError(err, { rethrowValidation: false });
    }
  };

  const deleteCards = async (data) => {
    try {
      const res = await instance.post('/card/delete', data);
      if (!handleResponse(res)) return false;
      refetchCardsLater();
      dispatch(setSelected([]));
      return true;
    } catch (err) {
      return handleRequestError(err, { rethrowValidation: false });
    }
  };

  const archiveCards = async (data) => {
    try {
      const res = await instance.post('/card/archive', data);
      if (!handleResponse(res)) return false;
      dispatch(fetchCards());
      dispatch(setSelected([]));
      return true;
    } catch (err) {
      return handleRequestError(err, { rethrowValidation: false });
    }
  };

  const unarchiveCards = async (data) => {
    try {
      const res = await instance.post('/card/unarchive', data);
      if (!handleResponse(res)) return false;
      dispatch(fetchCards());
      dispatch(setSelected([]));
      return true;
    } catch (err) {
      return handleRequestError(err, { rethrowValidation: false });
    }
  };

  const fetchCardSensitiveData = async (cardId) => {
    const res = await instance.get(
      `/card/card-sensitive-data?card_id=${cardId}`,
    );
    if (!res.data?.status) {
      throw new Error(res.data?.error || 'Unknown error');
    }
    return res.data;
  };

  const editCard = async (data) => {
    try {
      const res = await instance.post('/card/edit', data);
      if (!handleResponse(res)) return false;
      dispatch(fetchCards());
      return true;
    } catch (err) {
      const errorData = err?.response?.data;
      if (errorData?.success === false && err?.response?.status === 422) {
        showToaster('error', errorData.message);
        return false;
      }
      return handleRequestError(err);
    }
  };

  const getCardDetails = async (cardId) => {
    try {
      const res = await instance.get(`/card/cards/${cardId}`);
      return res?.data;
    } catch (err) {
      //todo: это для ситуации когда запрашивается карта которая не существует в таблице транзакций
      // showToaster('error', err?.response?.data?.message);
      showToaster('error', 'Something went wrong');
    }
  };

  const changeOwner = async (data) => {
    try {
      const res = await instance.post('/card/change_owner', data);
      if (!handleResponse(res)) return false;
      dispatch(fetchCards());
      return true;
    } catch (err) {
      return handleRequestError(err);
    }
  };

  const changeLimits = async (data) => {
    try {
      const res = await instance.post('/card/change-limits', data);
      if (!handleResponse(res)) return false;
      refetchCardsLater();
      return true;
    } catch (err) {
      return handleRequestError(err);
    }
  };

  return {
    createCard,
    freezeCards,
    unfreezeCards,
    blockCards,
    deleteCards,
    archiveCards,
    unarchiveCards,
    fetchCardSensitiveData,
    editCard,
    getCardDetails,
    changeOwner,
    changeLimits,
  };
}
